import { ApiResponseError } from "@/lib/error";
import { api } from "@/openapi/client";
import { userAdminQueryKeys } from "@/app/admin/users/_api/user-admin-query-keys";
import { getCacheTags } from "@/lib/cache-utils";
import type { operations, components } from "@/openapi/generated";
import { CustomClaim } from "@/openapi/generated";
export { CustomClaim, UserStatus } from "@/openapi/generated";

export type UserAdminListDto = components["schemas"]["UserListDto"];
export type PagedUsersResponse = components["schemas"]["PagedResponseUserListDto"];
export type UserAdminStatsDto = components["schemas"]["UserStatsDto"];
export type UserAdminDetailDto = components["schemas"]["UserAdminDetailDto"];

export type FilterParams = {
  searchTerm?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  emailVerified?: boolean;
  anonymous?: boolean;
  customClaims?: CustomClaim[];
};

export type ListUsersParams = FilterParams & {
  page?: number;
  size?: number;
  sort?: string[];
};

export async function setUserRoles(
  id: string,
  roles: CustomClaim[],
  client: typeof api = api
): Promise<UserAdminDetailDto> {
  const { data, error } = await client.PUT("/users/{id}/roles", {
    params: { path: { id } },
    body: roles,
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("setUserRoles");
  return data as UserAdminDetailDto;
}

export async function getAdminUser(
  id: string,
  client: typeof api = api
): Promise<UserAdminDetailDto> {
  const { data, error } = await client.GET("/users/{id}", { params: { path: { id } } });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getAdminUser");
  return data as UserAdminDetailDto;
}

export async function getAdminUsers(
  query: ListUsersParams,
  client: typeof api = api
): Promise<PagedUsersResponse> {
  const { data, error } = await client.GET("/users", {
    params: { query: query as operations["listUsers"]["parameters"]["query"] },
    next: { tags: getCacheTags(userAdminQueryKeys.list(query)) },
    cache: "force-cache",
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getAdminUsers");
  return data as PagedUsersResponse;
}

export async function getAdminUserStats(
  query: FilterParams,
  client: typeof api = api
): Promise<UserAdminStatsDto> {
  const { data, error } = await client.GET("/users/stats", {
    params: { query: query as operations["getStats"]["parameters"]["query"] },
    next: { tags: getCacheTags(userAdminQueryKeys.stats(query)) },
    cache: "force-cache",
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getAdminUserStats");
  return data as UserAdminStatsDto;
}
